'use strict';

import {ISmsSettings} from '../../models/AccountSettings';
import {ICallback, IPromptCallback, ISmsData} from './ts-sms.settings';
import {InviteEventsLogger} from '../../states/invite-new/invite.flow.logger';
import {IBrowserUtilsService} from '@techsee/techsee-client-infra/lib/services/BrowserUtilsService';
import {ISmsService} from '@techsee/techsee-client-infra/lib/services/smsService';
import {IRoomService} from '@techsee/techsee-client-infra/lib/services/RoomService';
import {SendFullSmsFlowService} from './SendFullSmsFlowService';
import {SmsStatusParams} from '../Session/SessionContracts';

export interface IClientAsDashboardSMSParams {
    roomId: string;
    phone: string;
    countryCode: string;
    link: string;
}

export class TsSmsService {
    private sendSmsFlowService?: SendFullSmsFlowService;
    private inviteFlowLogger?: InviteEventsLogger;
    private _smsService?: ISmsService;
    private _roomService?: IRoomService;
    private _smsSettings?: ISmsSettings;
    private _browserUtilsService?: IBrowserUtilsService;

    init(
        smsService: ISmsService,
        roomService: IRoomService,
        accountSmsSettings: ISmsSettings,
        eventsLogService: any,
        accountId: string,
        agentId: string,
        keepCustomerMobile: boolean,
        browserUtilsService: IBrowserUtilsService
    ) {
        this._smsService = smsService;
        this._roomService = roomService;
        this._smsSettings = accountSmsSettings;
        this._browserUtilsService = browserUtilsService;

        this.inviteFlowLogger = new InviteEventsLogger(eventsLogService, accountId, agentId, {
            keepCustomerMobile
        });
    }

    sendSMS(smsData: ISmsData, eventCb: ICallback, allowPrompt: boolean, promptCb: IPromptCallback) {
        if (!this._smsService || !this._roomService || !this._smsSettings || !this._browserUtilsService) {
            throw new Error('TsSmsService is not initialized, please ensure you execute init method.');
        }

        this.sendSmsFlowService = new SendFullSmsFlowService(
            this._smsService,
            this._roomService,
            this._smsSettings,
            this.inviteFlowLogger!,
            this._browserUtilsService
        );

        return this.sendSmsFlowService.sendSMS(smsData, eventCb, allowPrompt, promptCb, false);
    }

    sendNotificationSms(smsData: any) {
        if (!this._smsService) {
            throw new Error('TsSmsService is not initialized, please ensure you execute init method.');
        }

        return this._smsService.sendSimpleSms(smsData);
    }

    sendSimpleSms(smsData: any) {
        if (!this._smsService) {
            throw new Error('TsSmsService is not initialized, please ensure you execute init method.');
        }

        return this._smsService.sendSimpleSms(smsData);
    }

    smsStatusChanged(data: SmsStatusParams, progressCb: ICallback): void {
        this.sendSmsFlowService?.smsStatusChanged(data, progressCb);
    }

    restoreSms(data: any, progressCb: ICallback): void {
        this.sendSmsFlowService?.restoreSms(data, progressCb);
    }

    setRoomId(roomId: string) {
        if (!this.inviteFlowLogger) {
            throw new Error('TsSmsService is not initialized, please ensure you execute init method.');
        }

        this.inviteFlowLogger.setRoomId(roomId);
    }

    cleanup() {
        this.sendSmsFlowService?.dispose();
    }

    sendClientAsDashboard(data: IClientAsDashboardSMSParams) {
        if (!this._smsService) {
            throw new Error('TsSmsService is not initialized, please ensure you execute init method.');
        }

        return this._smsService.sendClientAsDashboardSMS(data);
    }

    sendObserverInvitation(data: IClientAsDashboardSMSParams) {
        if (!this._smsService) {
            throw new Error('TsSmsService is not initialized, please ensure you execute init method.');
        }

        return this._smsService.sendObserverInvitationSMS(data);
    }

    sendVisualJourney(data: any) {
        if (!this._smsService) {
            throw new Error('TsSmsService is not initialized, please ensure you execute init method.');
        }

        return this._smsService.sendVisualJourneySMS(data);
    }

    sendWarmTransferClientLink(data: {roomId: string}) {
        if (!this._smsService) {
            throw new Error('TsSmsService is not initialized, please ensure you execute init method.');
        }

        return this._smsService.sendWarmTransferClientLinkSMS(data);
    }
}
